// Game configuration data shared across the Betting flow.
// Extracted from Betting.js to reduce that file's footprint.

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GAME_TYPES = [
  { value: 'nassau', label: 'Nassau', desc: 'Front 9 / Back 9 / Overall with presses', icon: '3x', players: '2-4', teamable: true },
  { value: 'best_ball', label: 'Best Ball', desc: 'Team game — best individual score counts', icon: 'BB', players: '4', teamable: true, requireTeams: true },
  { value: 'scramble', label: 'Scramble', desc: 'Team plays from the best shot', icon: 'SC', players: '4', teamable: true, requireTeams: true },
  { value: 'skins', label: 'Skins', desc: 'Lowest score wins the pot. Ties carry over', icon: '$', players: '2-8' },
  { value: 'wolf', label: 'Wolf', desc: 'Rotating wolf picks a partner or goes lone', icon: 'W', players: '4' },
  { value: 'bingo_bango_bongo', label: 'Bingo Bango Bongo', desc: 'Points for first on, closest, first in', icon: 'B', players: '3-4' },
  { value: 'match_play', label: 'Match Play', desc: 'Head to head — win individual holes', icon: 'H', players: '2', teamable: true },
  { value: 'stableford', label: 'Stableford', desc: 'Points: Eagle 4, Birdie 3, Par 2, Bogey 1', icon: 'P', players: '2-8' },
  { value: 'hammer', label: 'Hammer', desc: 'Double the stakes — accept or drop', icon: '!', players: '2' },
  { value: 'dots', label: 'Dots', desc: 'Points for low score + birdies each hole', icon: 'D', players: '2-8' },
  { value: 'trash', label: 'Trash / Junk', desc: 'Greenies, sandies, barkies & more', icon: 'T', players: '2-8' },
];

export const SIDE_BET_TYPES = [
  { value: 'greenie', label: 'Greenie', desc: 'Hit the green on a par 3' },
  { value: 'sandie', label: 'Sandie', desc: 'Up and down from sand' },
  { value: 'barkie', label: 'Barkie', desc: 'Par+ after hitting a tree' },
  { value: 'closest_to_pin', label: 'CTP', desc: 'Closest tee shot par 3' },
  { value: 'long_drive', label: 'Long Drive', desc: 'Longest on the hole' },
];

export const GAME_RULES = {
  nassau: {
    title: 'Nassau',
    summary: 'Three separate bets in one: Front 9, Back 9, and Overall 18.',
    sections: [
      { heading: 'How It Works', text: 'Each nine and the overall are independent match-play bets. The player (or team) who wins the most holes in each segment wins that bet.' },
      { heading: 'Presses', text: 'A player who is losing can "press" to start a new side bet from the current hole forward. The press is worth the same stake as the original bet.' },
      { heading: 'Teams', text: 'In team Nassau, the best individual score on each hole counts for the team (best ball). The winning team splits the payout evenly.' },
      { heading: 'Payout', text: 'Up to 3 payouts per round (front, back, overall), plus any presses. Example: $10 Nassau = $10 for front winner, $10 for back, $10 for overall.' },
    ],
  },
  best_ball: {
    title: 'Best Ball',
    summary: 'Team game — each team uses their best individual score on every hole.',
    sections: [
      { heading: 'How It Works', text: 'All players play their own ball. On each hole, the lowest score from each team is compared. The team with the lower best-ball score wins the hole.' },
      { heading: 'Teams', text: 'Typically 2v2, but can be 3v1. Teams are set before the round begins.' },
      { heading: 'Payout', text: 'The team that wins more holes wins the match. The payout is the hole differential multiplied by the stake, split evenly among team members.' },
    ],
  },
  scramble: {
    title: 'Scramble',
    summary: 'Team game — everyone plays from the best shot each time.',
    sections: [
      { heading: 'How It Works', text: 'All players tee off. The team picks the best shot, and everyone plays their next shot from that spot. Repeat until the ball is holed. Enter individual scores — the app picks the best for each team.' },
      { heading: 'Teams', text: 'Typically 2v2. Each team collaborates to produce the lowest possible score.' },
      { heading: 'Payout', text: 'Same as Best Ball — team with more holes won takes the stake, split among members.' },
    ],
  },
  skins: {
    title: 'Skins',
    summary: 'Lowest score on each hole wins the "skin." Ties carry over.',
    sections: [
      { heading: 'How It Works', text: 'Each hole is worth one skin. The player (or team) with the lowest score wins it outright. If two or more players tie for the low score, the skin carries over to the next hole, making it worth double (or more).' },
      { heading: 'Carryovers', text: 'Skins accumulate on ties. A skin that has carried over 3 holes is worth 4x the stake when finally won.' },
      { heading: 'Teams', text: 'In team skins, the best score from each team is compared. The team that wins the skin splits the payout.' },
      { heading: 'Payout', text: 'Each skin is worth the stake. A player/team\'s total payout = number of skins won × stake.' },
    ],
  },
  wolf: {
    title: 'Wolf',
    summary: 'Rotating "Wolf" picks a partner after seeing tee shots — or goes alone for double.',
    sections: [
      { heading: 'How It Works', text: 'Players rotate being the Wolf in a set order. The Wolf tees off first and watches each player\'s tee shot. After any player hits, the Wolf can pick them as a partner for the hole. If the Wolf doesn\'t pick anyone, they go "Lone Wolf" against the other three.' },
      { heading: 'Lone Wolf', text: 'Going Lone Wolf doubles the stakes. If the Wolf wins alone, they collect from all three opponents. If they lose, they pay all three.' },
      { heading: 'Scoring', text: 'The Wolf + partner\'s best ball vs. the other two\'s best ball. Low score wins the hole.' },
      { heading: 'Payout', text: 'Standard: stake per hole. Lone Wolf: 2x stake per hole.' },
    ],
  },
  bingo_bango_bongo: {
    title: 'Bingo Bango Bongo',
    summary: 'Three points available on every hole: first on green, closest to pin, first to hole out.',
    sections: [
      { heading: 'Bingo', text: 'First player to get their ball on the green. Doesn\'t matter how — chip, pitch, or approach.' },
      { heading: 'Bango', text: 'Once all players are on the green, the player closest to the pin wins Bango.' },
      { heading: 'Bongo', text: 'First player to hole out (finish the hole) wins Bongo.' },
      { heading: 'Teams', text: 'In team BBB, points are aggregated per team and the team with more points wins.' },
      { heading: 'Payout', text: 'Each point is worth the stake. Net points determine who pays whom.' },
    ],
  },
  match_play: {
    title: 'Match Play',
    summary: 'Head to head — win individual holes, not total strokes.',
    sections: [
      { heading: 'How It Works', text: 'Each hole is a separate contest. The player (or team) with the lowest score wins the hole. Ties are halved — no one wins.' },
      { heading: 'Terminology', text: '"2 Up" means leading by 2 holes. "All Square" means tied. A match can be won early if a player leads by more holes than remain (e.g., "3 and 2" = 3 up with 2 to play).' },
      { heading: 'Teams', text: 'In team match play, best ball per team determines the hole winner.' },
      { heading: 'Payout', text: 'Each hole won is worth the stake. Net holes determine the payout.' },
    ],
  },
  stableford: {
    title: 'Stableford',
    summary: 'Points-based scoring: more points for better scores relative to par.',
    sections: [
      { heading: 'Point System', text: 'Eagle or better: 4 points. Birdie: 3 points. Par: 2 points. Bogey: 1 point. Double bogey or worse: 0 points.' },
      { heading: 'Strategy', text: 'Unlike stroke play, there\'s no penalty for a blowup hole (it\'s just 0 points). This encourages aggressive play — go for the birdie!' },
      { heading: 'Teams', text: 'In team Stableford, points are aggregated per team.' },
      { heading: 'Payout', text: 'Each point is worth the stake. Net point difference determines the payout.' },
    ],
  },
  hammer: {
    title: 'Hammer',
    summary: '2-player game — "hammer" your opponent to double the hole\'s value.',
    sections: [
      { heading: 'How It Works', text: 'Each hole starts at the base stake. At any point, either player can "hammer" to double the current value. The opponent must accept (play for the doubled amount) or drop (concede the hole at the current value).' },
      { heading: 'Re-Hammer', text: 'After being hammered, the other player can hammer back, doubling again. This can go back and forth.' },
      { heading: 'Strategy', text: 'Hammer when you feel confident — after a great drive or when your opponent is in trouble. But beware the re-hammer!' },
      { heading: 'Payout', text: 'Each hole is settled independently at whatever the hammer value reached.' },
    ],
  },
  dots: {
    title: 'Dots (Banker)',
    summary: 'Points awarded for low score and birdies on each hole.',
    sections: [
      { heading: 'How It Works', text: 'On each hole: 1 dot for the outright lowest score, 2 bonus dots for birdie or better. Ties for low score = no dot awarded.' },
      { heading: 'Teams', text: 'In team dots, dots are aggregated per team and settled at the team level.' },
      { heading: 'Payout', text: 'Each dot is worth the stake. Net dots determine payouts. A player who racks up birdies can win big.' },
    ],
  },
  trash: {
    title: 'Trash / Junk',
    summary: 'Collection of mini-bets: greenies, sandies, barkies, and more.',
    sections: [
      { heading: 'Greenie', text: 'Hit the green in regulation on a par 3. Closest to the pin among those on the green wins.' },
      { heading: 'Sandie', text: 'Make par or better after being in a bunker.' },
      { heading: 'Barkie', text: 'Make par or better after hitting a tree.' },
      { heading: 'Other Junk', text: 'Closest to Pin (CTP): closest tee shot on par 3s. Long Drive: longest drive on designated holes. You can add any custom junk bets your group likes.' },
      { heading: 'Payout', text: 'Each piece of "junk" is worth the stake. The player who collects it wins the stake from each opponent.' },
    ],
  },
};
